import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger'
import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsNumber,
  IsUUID,
  IsDateString,
  IsEnum,
  IsObject,
  ValidateNested,
  IsArray,
} from 'class-validator'
import { PedagogicContentState } from '../../../enums/content.enum'
import { SubjectDto } from '../../subjects'
import { ClassDto } from '../../classes/'
import { GroupDto } from '../../group'
import { TeachingCapabilities } from '@sphere/shared/domain'
import { TeachingStaffDto } from '../../teaching-staff'
import { Type } from 'class-transformer'
import { SchoolYearRefDto } from '../../references/response'
import { LevelDto } from '../../levels/response'
import { SubjectTypeDto } from '../../subject-types'
import { EstablishmentDto } from '../../establishments/response'
export class TeachingDto {
  @ApiProperty({
    description: "ID de l'enseignement",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  id!: string

  @ApiProperty({
    description: 'ID de la classe',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => ClassDto,
  })
  @IsNotEmpty()
  @IsUUID('4')
  class!: ClassDto

  @ApiProperty({
    description: 'ID du groupe',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => GroupDto,
  })
  @IsNotEmpty()
  @IsUUID('4')
  group!: GroupDto

  @ApiProperty({
    description: "ID de l'établissement",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  establishmentId!: string

  @ApiProperty({
    description: "Établissement",
    type: () => EstablishmentDto,
  })
  @IsNotEmpty()
  @ValidateNested()
  @Type(() => EstablishmentDto)
  establishment!: EstablishmentDto

  @ApiProperty({
    description: 'État',
    enum: PedagogicContentState,
    example: PedagogicContentState.ACTIVE,
  })
  @IsNotEmpty()
  @IsEnum(PedagogicContentState)
  state!: PedagogicContentState

  @ApiProperty({
    description: "ID de l'année scolaire",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => SchoolYearRefDto,
  })
  @IsNotEmpty()
  @IsUUID('4')
  schoolYear!: SchoolYearRefDto


  @ApiProperty({
    description: 'Description',
    example: "Description de l'enseignement",
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  description!: string

  @ApiProperty({
    description: 'Poids dans la progression',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  weightInProgression!: number

  @ApiProperty({
    description: 'Charge de travail estimée',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  estimatedWorkload!: number

  @ApiProperty({
    description: 'ID du créateur',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  createdByUserId!: string

  @ApiProperty({
    description: 'Date de création',
    example: '2024-09-01',
  })
  @IsNotEmpty()
  @IsDateString()
  createdAt!: Date

  @ApiProperty({
    description: 'Date de mise à jour',
    example: '2024-09-01',
  })
  @IsNotEmpty()
  @IsDateString()
  updatedAt!: Date

  @ApiProperty({
    description: 'Date de suppression',
    example: '2024-09-01',
  })
  @IsOptional()
  @IsDateString()
  deletedAt?: Date

  @ApiProperty({
    description: "ID de l'utilisateur qui a supprimé",
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsOptional()
  @IsString()
  deletedByUserId?: string


  @ApiProperty({
    description: 'ID du programme version',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: 'string',
  })
  @IsNotEmpty()
  @IsUUID('4')
  programVersionId!: string

  @ApiProperty({
    description: 'ID de la matière',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => SubjectDto,
  })
  @IsNotEmpty()
  subject!: SubjectDto

  @ApiProperty({
    description: 'Label de la matière',
    example: 'Mathématiques',
    type: 'string',
  })
  @IsNotEmpty()
  @IsString()
  subjectLabel!: string

  @ApiPropertyOptional({
    description: 'Type de matière (dérivé du sujet)',
    type: () => SubjectTypeDto,
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => SubjectTypeDto)
  subjectType?: SubjectTypeDto

  @ApiProperty({
    description: 'ID du niveau pédagogique',
    example: '550e8400-e29b-41d4-a716-446655440000',
    type: () => LevelDto,
  })
  @IsNotEmpty()
  @IsUUID('4')
  level!: LevelDto


  @ApiPropertyOptional({ description: "Staff de l'enseignement", type: () => [TeachingStaffDto] })
  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => TeachingStaffDto)
  staff?: TeachingStaffDto[]

  @ApiProperty({
    description: 'Nombre de chapitres',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  chaptersCount!: number

  @ApiProperty({
    description: 'Nombre de leçons',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  lessonsCount!: number

  @ApiProperty({
    description: 'Nombre de ressources',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  resourcesCount!: number

  @ApiProperty({
    description: 'Nombre de staff',
    example: 10,
    type: Number,
  })
  @IsNotEmpty()
  @IsNumber()
  staffCount!: number

  @ApiProperty({
    description: 'Capabilities',
    type: Object,
    additionalProperties: true,
  })
  @IsNotEmpty()
  @IsObject()
  capabilities!: TeachingCapabilities
}
